import type {
  SecretConfirmAction,
  SecretEntry,
  SecretGrant,
  SecretGrantDraft,
  SecretGrantPermission,
  SecretKind,
} from './secret-dashboard-types'

export const SECRET_GRANT_PERMISSIONS: SecretGrantPermission[] = [
  'describe',
  'read',
  'reveal',
  'inject',
  'write',
  'grant',
]

export function createEmptySecretDraft(): { key: string; value: string; kind: SecretKind; description: string; reason: string } {
  return { key: '', value: '', kind: 'secret', description: '', reason: 'web create' }
}

export function createGrantDraft(entry?: SecretEntry): SecretGrantDraft {
  return {
    targetType: 'api_key',
    targetId: '',
    targetDisplayName: '',
    keyPattern: entry?.key ?? '*',
    permissions: ['reveal'],
    ttlDays: '',
    reason: 'web grant',
  }
}

export function normalizeSecretPath(path: string): string {
  const trimmed = path.trim()
  if (!trimmed) return '/'
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`
}

export function togglePermission(
  current: SecretGrantPermission[],
  permission: SecretGrantPermission,
): SecretGrantPermission[] {
  return current.includes(permission)
    ? current.filter((item) => item !== permission)
    : [...current, permission]
}

export function ttlDaysToEpochMs(ttlDays: string, now = Date.now()): number | null {
  const trimmed = ttlDays.trim()
  if (!trimmed) return null
  const days = Number(trimmed)
  if (!Number.isFinite(days) || days <= 0) return null
  return now + Math.round(days * 86_400_000)
}

export function formatSecretTimestamp(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return 'Never'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Unknown'
  return date.toLocaleString()
}

export function keyMatchesPattern(pattern: string, key: string): boolean {
  if (pattern === '*' || pattern === key) return true
  if (pattern.endsWith('*')) return key.startsWith(pattern.slice(0, -1))
  return false
}

function pathMatches(grantPath: string, entryPath: string): boolean {
  const grant = normalizeSecretPath(grantPath)
  const entry = normalizeSecretPath(entryPath)
  if (grant === '/' || grant === entry) return true
  return entry.startsWith(`${grant.replace(/\/$/, '')}/`)
}

function isGrantExpired(expiresAt: number | null): boolean {
  return expiresAt !== null && expiresAt <= Date.now()
}

export function grantTargetsEntry(grant: SecretGrant, entry: SecretEntry): boolean {
  if (grant.revokedAt) return false
  if (isGrantExpired(grant.expiresAt)) return false
  if (grant.environment !== entry.environment) return false
  if (!pathMatches(grant.path, entry.path)) return false
  return keyMatchesPattern(grant.keyPattern, entry.key)
}

export function grantsForEntry(grants: SecretGrant[], entry: SecretEntry): SecretGrant[] {
  return grants.filter((grant) => grantTargetsEntry(grant, entry))
}

export function permissionLabel(permissions: SecretGrantPermission[]): string {
  return permissions.length > 0 ? permissions.join(', ') : 'none'
}

export function confirmTitle(action: SecretConfirmAction | null): string {
  if (!action) return 'Confirm action'
  if (action.type === 'disable') return `Disable ${action.entry.key}?`
  if (action.type === 'destroy') return `Destroy ${action.entry.key}?`
  if (action.type === 'revoke-grant') return `Revoke ${action.grant.targetDisplayName}?`
  return `Rollback to v${action.version.version}?`
}

export function confirmDescription(action: SecretConfirmAction | null): string {
  if (!action) return ''
  if (action.type === 'destroy') return 'This crypto-shreds current and historical ciphertext and cannot be undone.'
  if (action.type === 'disable') return 'This removes the active entry from normal list and lookup results.'
  if (action.type === 'revoke-grant') return 'The target loses this grant immediately.'
  return 'Rollback creates a new version from the selected historical value.'
}

export function actionSuccessText(action: SecretConfirmAction): string {
  if (action.type === 'disable') return 'Secret disabled'
  if (action.type === 'destroy') return 'Secret destroyed'
  if (action.type === 'revoke-grant') return 'Grant revoked'
  return 'Rollback queued'
}

export function isDangerAction(action: SecretConfirmAction | null): boolean {
  return action?.type === 'destroy' || action?.type === 'disable'
}
